import { ApiResponse } from '../schemas/apiResponseSchema';
import apiClient from '../clients/apiClient';
import { Schemas } from '../types/api-types';

type CashierSignInRequest = Schemas['CashierSignInRequest']
type CashierSignInResponse = Schemas['CashierSignInResponse']
type CashierStatusResponse = Schemas['CashierStatusResponse']
type CashierSignOffDataResponse = Schemas['CashierSignOffDataResponse']
type CashierSignOffRequest = Schemas['CashierSignOffRequest']
type CachierSignOffResponse = Schemas['CashierSignOffResponse']

type CashierSignedInSessionResponse = {
    it_cashier_session_id: number;
    it_cashier_session_cash_book_id: number;
    it_user_master_id: number;
    user_name: string;
}

type CashierPendingCashInRequest = {
    it_cashier_session_id: number;
    amount: number;
}

type CashierPendingCashInItem = {
    id: number;
    cash_in: number;
    detail?: string | null;
}

type CashierPendingCashInResponse = {
    total_cash_in: number;
    items: CashierPendingCashInItem[];
}

type CashierCashInCreateResponse = {
    it_cashier_session_details_id: number;
}

type CashierAcceptCashInResponse = {
    total_cash_in: number;
    updated_count: number;
}

type CashierCashOutRequest = {
    receiver_cashier_session_id: number;
    amount: number;
}

type CashierCashOutResponse = {
    sender_details_id: number;
    receiver_details_id: number;
}

export async function cashierSignIn(reuest: CashierSignInRequest): Promise<CashierSignInResponse> {
    try {
        const response: ApiResponse<CashierSignInResponse> = (await apiClient.post('/cashier/sign_in', { ...reuest })).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Cashier sign-in failed');
        }
        throw new Error('network error, please contact support team');
    }

}

export async function getCashierStatus(): Promise<CashierStatusResponse> {
    try {
        const response: ApiResponse<CashierStatusResponse> = (await apiClient.get('/cashier/status')).data;
        return response.data;
    } catch (error: any) {
        throw error;
    }

}

export async function getCashierSignOutStatus(): Promise<CashierSignOffDataResponse> {
    try {
        const response: ApiResponse<CashierSignOffDataResponse> = (await apiClient.get('/cashier/sign_off')).data;
        return response.data;
    } catch (error: any) {
        throw error;
    }

}

export async function cashierSignOff(reuest: CashierSignOffRequest): Promise<CachierSignOffResponse> {
    try {
        const response: ApiResponse<CachierSignOffResponse> = (await apiClient.post('/cashier/sign_off', { ...reuest })).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Cashier sign-off failed');
        }
        throw new Error('network error, please contact support team');
    }

}

export async function getSignedInCashiers(): Promise<CashierSignedInSessionResponse[]> {
    try {
        const response: ApiResponse<CashierSignedInSessionResponse[]> = (await apiClient.get('/cashier/signed_in_sessions')).data;
        return response.data || [];
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Failed to load signed-in cashiers');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function createPendingCashIn(request: CashierPendingCashInRequest): Promise<CashierCashInCreateResponse> {
    try {
        const response: ApiResponse<CashierCashInCreateResponse> = (await apiClient.post('/cashier/cash_in', { ...request })).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Failed to create cash-in request');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getPendingCashIn(): Promise<CashierPendingCashInResponse> {
    try {
        const response: ApiResponse<CashierPendingCashInResponse> = (await apiClient.get('/cashier/pending_cash_in')).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Failed to load pending cash-in');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function acceptPendingCashIn(): Promise<CashierAcceptCashInResponse> {
    try {
        const response: ApiResponse<CashierAcceptCashInResponse> = (await apiClient.post('/cashier/accept_cash_in')).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Failed to accept pending cash-in');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function cashierCashOut(request: CashierCashOutRequest): Promise<CashierCashOutResponse> {
    try {
        const response: ApiResponse<CashierCashOutResponse> = (await apiClient.post('/cashier/cash_out', { ...request })).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Failed to process cash out');
        }
        throw new Error('network error, please contact support team');
    }
}

export type CashierSessionDetailResponse = {
    it_cashier_session_id: number;
    user_name: string;
    real_name: string;
    status: string;
    sign_in_time: string | null;
    sign_off_time: string | null;
}

export async function getAllCashierSessions(): Promise<CashierSessionDetailResponse[]> {
    try {
        const response: ApiResponse<CashierSessionDetailResponse[]> = (await apiClient.get('/cashier/sessions')).data;
        return response.data || [];
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Failed to load cashier sessions');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function forceSignOffCashier(sessionId: number): Promise<any> {
    try {
        const response: ApiResponse<any> = (await apiClient.post(`/cashier/force_sign_off/${sessionId}`)).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Failed to force sign-off cashier');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getSessionSignOffData(sessionId: number): Promise<any> {
    try {
        const response: ApiResponse<any> = (await apiClient.get(`/cashier/sessions/${sessionId}/sign_off_data`)).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Failed to fetch cashier session sign-off data');
        }
        throw new Error('network error, please contact support team');
    }
}
